// Client for the /spotify/* token-broker. The Lambda owns the OAuth secret +
// refresh token; this service fetches short-lived access tokens (cached until
// ~1 min before expiry) for the SONIC controller to call the Web API directly,
// and drives the link/unlink flow. All endpoints are Cognito-authed.

const API_BASE_URL = "https://api.serga.ba";

// OAuth authorize config. The client id is PUBLIC (it appears in the authorize
// URL the user is sent to) — only the client *secret* is sensitive, and that
// stays server-side. Building the URL here lets us navigate to Spotify
// synchronously inside the click handler; doing it after an `await fetch(...)`
// loses the click's user-activation and Safari drops the cross-origin nav.
const SPOTIFY_CLIENT_ID = "3ca271093ceb4a48814bd6bf1960fb57";
const SPOTIFY_REDIRECT_URI = "https://serga.ba/spotify/callback";
const SPOTIFY_SCOPES = [
  "user-read-playback-state",
  "user-modify-playback-state",
  "user-read-currently-playing",
  "user-read-recently-played",
  "playlist-read-private",
];

export interface SpotifyStatus {
  linked: boolean;
  premium: boolean;
  account?: string;
}

export class SpotifyService {
  private static authTokens: any = null;
  private static tokenCache: { value: string; expiresAt: number } | null = null;

  static setAuthTokens(tokens: any) {
    this.authTokens = tokens;
  }

  private static getAuthHeaders() {
    if (!this.authTokens?.idToken) throw new Error("No authentication token available");
    return {
      Authorization: `Bearer ${this.authTokens.idToken}`,
      "Content-Type": "application/json",
    };
  }

  private static async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: { ...this.getAuthHeaders(), ...(options.headers || {}) },
    });
    if (!response.ok) {
      const errorBody = await response.text();
      let message = `Request failed: ${response.status}`;
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed.error) message = parsed.error;
      } catch {}
      throw new Error(message);
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  /** { linked, premium } — drives the tray's empty/linked state. */
  static async getStatus(): Promise<SpotifyStatus> {
    return this.request<SpotifyStatus>("/spotify/status");
  }

  /** The Spotify consent URL (built client-side from public config). */
  static authorizeUrl(): string {
    const p = new URLSearchParams({
      client_id: SPOTIFY_CLIENT_ID,
      response_type: "code",
      redirect_uri: SPOTIFY_REDIRECT_URI,
      scope: SPOTIFY_SCOPES.join(" "),
      state: "serga",
      show_dialog: "false",
    });
    return `https://accounts.spotify.com/authorize?${p.toString()}`;
  }

  /** Kick off OAuth — navigate to Spotify. SYNCHRONOUS so it runs inside the
   *  click handler (no `await` first), preserving user-activation for the
   *  cross-origin navigation (Safari drops it otherwise). */
  static beginLogin(): void {
    window.location.assign(this.authorizeUrl());
  }

  /** Complete the redirect: exchange the code for tokens (server-side). */
  static async completeLink(code: string): Promise<{ linked: boolean }> {
    this.tokenCache = null;
    return this.request<{ linked: boolean }>("/spotify/link", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  static async unlink(): Promise<void> {
    this.tokenCache = null;
    await this.request("/spotify/link", { method: "DELETE" });
  }

  /** A fresh access token, cached until ~1 min before it expires. */
  static async getToken(): Promise<string> {
    if (this.tokenCache && this.tokenCache.expiresAt > Date.now()) {
      return this.tokenCache.value;
    }
    const tok = await this.request<{ access_token: string; expires_in: number }>("/spotify/token");
    this.tokenCache = {
      value: tok.access_token,
      expiresAt: Date.now() + Math.max(0, (tok.expires_in - 60) * 1000),
    };
    return tok.access_token;
  }

  static clearTokenCache() {
    this.tokenCache = null;
  }
}
